import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../components/AuthContext';
import { api } from '../../lib/api';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [plan, setPlan] = useState('basic');
  const [wizardStep, setWizardStep] = useState(0); // 0=hidden, 1=choice, 2=plans
  const [wizardDone, setWizardDone] = useState(
    localStorage.getItem('setup_wizard_done') === 'true'
  );


  useEffect(() => {
    api.get('/restaurants').then(data => {
      if (data.length > 0) {
        setRestaurant(data[0]);
        setPlan(data[0].plan || 'basic');
        // ── Redirect new restaurants to setup wizard ──────────────
        api.get(`/restaurants/${data[0].restaurant_id}/items`).then(items => {
          if (items.length === 0) {
            window.location.href = '/admin/setup';
            return;
          }
        }).catch(() => {});
        api.get(`/restaurants/${data[0].restaurant_id}/stats`).then(s => {
          setStats(s);

          // Show wizard only if menu is empty and wizard not completed
          if (s.total_items === 0 && localStorage.getItem('setup_wizard_done') !== 'true') {
            setWizardStep(1);
          }
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const doneWizard = () => {
    localStorage.setItem('setup_wizard_done', 'true');
    setWizardDone(true);
    setWizardStep(0);
  };

  const setupPlans = [
    {
      key: 'pro',
      name: 'Pro Setup',
      price: '$119',
      badge: '⭐ Most Popular',
      badgeColor: '#d97706',
      border: '#f59e0b',
      bg: '#fffbeb',
      url: process.env.REACT_APP_LS_PRO_SETUP_URL || '/setup',
      features: ['Everything in Basic', 'Online ordering setup', 'Catering form activated', 'Order test completed', '2 revisions + priority support'],
    },
    {
      key: 'basic',
      name: 'Basic Setup',
      price: '$99',
      badge: null,
      border: '#1a5c2a',
      bg: '#f0fdf4',
      url: process.env.REACT_APP_LS_BASIC_SETUP_URL || '/setup',
      features: ['Full menu setup', 'Food photos uploaded', 'Opening hours configured', 'QR code generated & tested', '1 revision included'],
    },
  ];

  return (
    <AdminLayout title="Dashboard">

      {/* ── Setup Wizard Modal ── */}
      {wizardStep > 0 && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: 'white', borderRadius: 20, width: '100%', maxWidth: 560,
            boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden',
          }}>

            {/* Step 1 — Choice */}
            {wizardStep === 1 && (
              <>
                <div style={{ background: 'linear-gradient(135deg, #0a1f0f, #1a5c2a)', padding: '32px 28px', textAlign: 'center' }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>👋</div>
                  <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 800, color: 'white', marginBottom: 8 }}>
                    Welcome to QR Digi Menu!
                  </h2>
                  <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                    Your account is ready. How would you like to set up your restaurant menu?
                  </p>
                </div>
                <div style={{ padding: '28px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                    {/* Option A — We do it */}
                    <button onClick={() => setWizardStep(2)} style={{
                      border: '2px solid #1a5c2a', borderRadius: 14, padding: '18px 20px',
                      background: '#f0fdf4', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ fontSize: 32 }}>🚀</div>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a5c2a', marginBottom: 4 }}>
                            Set it up for me — from $99
                          </div>
                          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                            We build your complete menu in 48 hours. Photos, categories, hours — all done for you.
                          </div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: 20, color: '#1a5c2a' }}>→</div>
                      </div>
                    </button>

                    {/* Option B — I'll do it */}
                    <button onClick={doneWizard} style={{
                      border: '2px solid #e5e7eb', borderRadius: 14, padding: '18px 20px',
                      background: 'white', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ fontSize: 32 }}>✏️</div>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginBottom: 4 }}>
                            I'll set it up myself
                          </div>
                          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                            Use the dashboard to add your menu items, categories and photos yourself.
                          </div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: 20, color: '#999' }}>→</div>
                      </div>
                    </button>
                  </div>
                  <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                    You can always get setup help later from the dashboard
                  </p>
                </div>
              </>
            )}

            {/* Step 2 — Choose plan */}
            {wizardStep === 2 && (
              <>
                <div style={{ background: 'linear-gradient(135deg, #0a1f0f, #1a5c2a)', padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <button onClick={() => setWizardStep(1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 20, cursor: 'pointer' }}>←</button>
                  <div>
                    <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: 'white', marginBottom: 4 }}>
                      Choose your setup package
                    </h2>
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                      One-time payment · We start within 24 hours · Done in 48 hours
                    </p>
                  </div>
                </div>
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {setupPlans.map(p => (
                    <div key={p.key} style={{ border: `2px solid ${p.border}`, borderRadius: 14, padding: '20px', background: p.bg, position: 'relative' }}>
                      {p.badge && (
                        <div style={{ position: 'absolute', top: -10, left: 20, background: p.badgeColor, color: 'white', fontSize: 11, fontWeight: 700, padding: '2px 12px', borderRadius: 20 }}>{p.badge}</div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>{p.name}</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: p.border }}>{p.price} <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>once</span></div>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        {p.features.map(f => (
                          <div key={f} style={{ fontSize: 13, color: '#444', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#1a5c2a', fontWeight: 700 }}>✓</span> {f}
                          </div>
                        ))}
                      </div>
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={doneWizard}
                        style={{
                          display: 'block', width: '100%', padding: '12px',
                          background: p.border, color: 'white',
                          borderRadius: 10, fontWeight: 700, fontSize: 14,
                          textAlign: 'center', textDecoration: 'none',
                          boxShadow: `0 4px 12px ${p.border}40`,
                        }}
                      >
                        Pay {p.price} — Get Started →
                      </a>
                    </div>
                  ))}
                  <button onClick={doneWizard} style={{
                    background: 'none', border: 'none', color: '#9ca3af',
                    fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
                  }}>
                    I'll set up myself for now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Main dashboard ── */}
      {!restaurant ? (
        <div className="card p-8 text-center">
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏪</div>
          <h2 className="text-xl font-semibold mb-2">Welcome to QR Menu!</h2>
          <p className="text-muted mb-6">Get started by creating your restaurant profile.</p>
          <Link to="/admin/settings" className="btn btn-primary btn-lg">Create Restaurant →</Link>
        </div>
      ) : (
        <>
          {/* Setup reminder strip — shows if wizard done but menu still empty */}
          {wizardDone && stats && stats.total_items === 0 && (
            <div style={{
              background: '#fffbeb', border: '1px solid #f59e0b',
              borderRadius: 12, padding: '12px 18px', marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
            }}>
              <div style={{ fontSize: 13, color: '#92400e' }}>
                🚀 <strong>Your menu is still empty.</strong> Need help setting it up?
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { localStorage.removeItem('setup_wizard_done'); setWizardDone(false); setWizardStep(1); }}
                  style={{ fontSize: 12, fontWeight: 700, color: '#d97706', background: 'white', border: '1px solid #f59e0b', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
                  View Setup Options
                </button>
                <Link to="/admin/menu" style={{ fontSize: 12, fontWeight: 600, color: '#666', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 14px', textDecoration: 'none' }}>
                  Add Items Myself
                </Link>
              </div>
            </div>
          )}

          <div className="stats-grid">
            {[
              { label: 'Categories', value: stats?.total_categories ?? '—', icon: '📂' },
              { label: 'Menu Items', value: stats?.total_items ?? '—', icon: '🍽️' },
              { label: 'Available Items', value: stats?.available_items ?? '—', icon: '✅' },
              { label: 'QR Scans', value: stats?.qr_scans ?? '—', icon: '📱' },

            ].map(s => (
              <div key={s.label} className="stat-card">
                <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value">{s.value}</div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 flex-wrap">
            <Link to="/admin/menu" className="btn btn-primary">+ Add Menu Item</Link>
            <Link to="/admin/qr" className="btn btn-outline">📱 View QR Code</Link>
            <a href={`${window.location.origin}/menu/${restaurant.slug}`} target="_blank" rel="noreferrer" className="btn btn-outline">
              👁️ Public Menu
            </a>
          </div>
        </>
      )}
    </AdminLayout>
  );
}